import { queryOptions, useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { sessionResourceAtom } from 'atoms/root';
import { api } from 'lib/request/api';
import { downloadFromUrl } from 'utils/download-blob';
import {
  useServerExport,
  type ExportParams,
  type ServerExportStatus,
} from './useServerExport';

// How often to poll the durable SpatialExport record while an export is still processing. The record
// is the AUTHORITATIVE source of truth for the one-off Pusher event (which a closed tab or a lost
// message can miss): the backend converges a stuck row to a terminal state (SpatialExport#fail_if_stale!
// on the read side), so a finished/failed export always resolves in the UI without a client-side timer
// that could fail a slow-but-alive export (INS-13263).
const POLL_INTERVAL_MS = 5000;

// The durable record returned by GET /spatial_exports/:token. The jbuilder camelizes every key
// (config/initializers/jbuilder.rb — `key_format(camelize: :lower)`), so the wire shape is camelCase
// and the `api` client does not convert case. Status values come from Lifecycle::ActiveRecord:
// pending → processing → processed | failed.
interface SpatialExportRecord {
  // One of Lifecycle's status strings: pending → processing → processed | failed.
  status: string;
  downloadUrl?: string | null;
  failureReason?: string | null;
  rowCount?: number | null;
}

// The export error envelope the backend returns, in either shape: a spatial export sends
// { errors: [{ message }] } or a bare { error }; a catalog export whose query fails validation sends
// attribute-keyed ActiveModel errors, { errors: { page_size: ["is not a number"] } }.
interface ExportErrorBody {
  errors?: { message?: string }[] | Record<string, string[]>;
  error?: string;
}

/**
 * The first thing a refused export said, whichever shape the envelope came in: an array of
 * `{ message }`, attribute-keyed ActiveModel errors (`{ page_size: ["is not a number"] }` → "page_size
 * is not a number"), or a bare `error`. Shared by every export surface (spatial + catalog) so they
 * read a refusal the same way.
 *
 * @param {ExportErrorBody | null | undefined} body - The parsed error body.
 * @return {string | undefined} The message, or undefined if the body carried none.
 */
export const exportErrorMessage = (
  body: ExportErrorBody | null | undefined
): string | undefined => {
  const errors = body?.errors;

  if (Array.isArray(errors)) {
    return errors[0]?.message ?? body?.error;
  }

  if (errors) {
    const [attribute, messages] = Object.entries(errors)[0] ?? [];
    if (attribute && messages?.[0]) {
      return `${attribute} ${messages[0]}`;
    }
  }

  return body?.error;
};

/**
 * Normalizes a failed export POST into an Error carrying the server's friendly message rather than
 * ky's. When the thrown value is an HTTP error it reads the response body and runs it through
 * {@link exportErrorMessage}; otherwise it returns the original error (or a generic one). The thrown
 * value is duck-typed on `response.json` so this shared helper needs no ky import (ky's `api` client is
 * confined to feature hooks.ts); the raw cause is preserved for logging.
 *
 * @param {unknown} error - The value thrown by the export POST.
 * @return {Promise<Error>} The error to surface, with a user-facing message.
 */
export const parseExportError = async (error: unknown): Promise<Error> => {
  const response = (error as { response?: { json?: () => Promise<unknown> } })
    ?.response;
  if (response && typeof response.json === 'function') {
    const data = (await response
      .json()
      .catch(() => null)) as ExportErrorBody | null;
    return new Error(exportErrorMessage(data) ?? 'Failed to start export', {
      cause: error,
    });
  }
  return error instanceof Error
    ? error
    : new Error('Failed to start export', { cause: error });
};

/**
 * Shared POST for a spatial (GIS) export: fires the request through the ky `api` client and, on
 * failure, rethrows the server's friendly message via {@link parseExportError}. Every spatial surface
 * (Map Query, Areas, Type Curves) calls this with only its own endpoint, so the transport + error
 * handling live in one place.
 *
 * @param {string} path - The spatial export endpoint (relative to the api base).
 * @param {ExportParams} [params] - The request body.
 * @return {Promise<{ exportId: string }>} The started export's token.
 */
export const postSpatialExport = async (
  path: string,
  params?: ExportParams
): Promise<{ exportId: string }> => {
  try {
    return await api.post(path, { json: params }).json<{ exportId: string }>();
  } catch (error) {
    throw await parseExportError(error);
  }
};

/**
 * Shared scaffolding for a page's spatial (GIS) export. Wires the caller's export POST (`exportFn`) to
 * the signed-in user's Pusher completion channel through {@link useServerExport}, and ALSO polls the
 * durable SpatialExport record (GET /spatial_exports/:token) as the authoritative backstop: the Pusher
 * completion event is one-off, so if it is lost the UI would sit on "Exporting…" forever; the record's
 * terminal status (processed/failed) then drives the banner and the download instead. There is NO
 * client-side timeout — a long-but-alive export must never be failed prematurely; the backend converges
 * a genuinely stuck row to `failed` (SpatialExport#fail_if_stale!), and the poll surfaces that. A
 * terminal Pusher status still wins; the poll only resolves a would-be-stuck export. The single
 * download effect (keyed on the export id) runs for whichever path resolves first, so a late Pusher
 * event after a poll-driven download never downloads twice.
 *
 * @param {object} config - The export configuration.
 * @param {string} config.event - The Pusher completion event name.
 * @param {(params?: ExportParams) => Promise<{ exportId: string }>} config.exportFn - Starts the export.
 * @return {object} The server-export status + a typed `startExport(request)` + the signed-in user id.
 */
export const useSpatialExport = <TRequest extends ExportParams>({
  event,
  exportFn,
}: {
  event: string;
  exportFn: (params?: ExportParams) => Promise<{ exportId: string }>;
}) => {
  const userId = useAtomValue(sessionResourceAtom)?.id;

  const { startExport, exportStatus, resetExport } = useServerExport({
    // Null before a session loads, so useServerExport skips the subscribe rather than binding the
    // literal `user_undefined_exports` channel.
    channel: userId ? `user_${userId}_exports` : null,
    event,
    exportFn,
    // Downloads are centralized in this hook (see the effect below) so the Pusher and poll paths can't
    // both fire; useServerExport must not auto-download on its own.
    autoDownload: false,
  });

  const start = useCallback(
    (request: TRequest) => startExport(request),
    [startExport]
  );

  const exportId = exportStatus.exportId;
  // A terminal Pusher status (complete/failed) means the live event already resolved it — no poll/merge.
  const pusherTerminal =
    exportStatus.status === 'complete' || exportStatus.status === 'failed';

  // Poll the durable record while we are still processing and have an id. Stops once the record is
  // terminal (processed/failed) — including when the backend's staleness rule converges a dead row —
  // or once the Pusher event resolves it and disables the query.
  const recordQuery = queryOptions({
    queryKey: ['spatial-export', exportId],
    queryFn: () =>
      api.get(`spatial_exports/${exportId}`).json<SpatialExportRecord>(),
  });
  const { data: record } = useQuery({
    ...recordQuery,
    enabled: exportStatus.status === 'processing' && Boolean(exportId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'processed' || status === 'failed'
        ? false
        : POLL_INTERVAL_MS;
    },
  });

  // Terminal Pusher status wins; otherwise a terminal record status resolves the export (and a dead row
  // the backend flipped to failed surfaces here too).
  const mergedStatus = useMemo<ServerExportStatus>(() => {
    if (pusherTerminal) return exportStatus;
    if (record?.status === 'processed') {
      return {
        ...exportStatus,
        status: 'complete',
        downloadUrl: record.downloadUrl ?? null,
        rowCount: record.rowCount ?? null,
        error: null,
      };
    }
    if (record?.status === 'failed') {
      return {
        ...exportStatus,
        status: 'failed',
        downloadUrl: null,
        error: record.failureReason ?? 'Export failed',
        rowCount: null,
      };
    }
    return exportStatus;
  }, [pusherTerminal, exportStatus, record]);

  // Auto-download once per export, for whichever path (Pusher or poll) resolved it first. Guarded by
  // the export id so a late Pusher event arriving after a poll-driven download never downloads twice.
  const downloadedRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      mergedStatus.status === 'complete' &&
      mergedStatus.downloadUrl &&
      exportId &&
      downloadedRef.current !== exportId
    ) {
      downloadedRef.current = exportId;
      downloadFromUrl(mergedStatus.downloadUrl);
    }
  }, [mergedStatus, exportId]);

  return {
    exportStatus: mergedStatus,
    resetExport,
    isExporting: mergedStatus.status === 'processing',
    startExport: start,
    userId,
  };
};
